import dayjs from "dayjs";
import { FILE_TYPES } from "../../../../shared/interfaces";
import { DocumentClass } from "../../../../shared/interfaces/enums/document-class-enum";
import { CaracteristicasDelSiniestro, ComplaintPdfServiceRequest, DaniosMaterialesCosas, Tercero } from "../../../../shared/interfaces/services/complaint-pdf-service";
import { ComplaintPdfDataResponse, Element } from "../../../../shared/interfaces/services/get-complaint-pdf-service";
import { FilenetDocumentRequest } from "../../../../shared/interfaces/services/get-document-service";
import ClaimService from "../../../../shared/services/claim";
import DocumentsService from "../../../../shared/services/documents";
import { downloadBase64Files, downloadPresignedFile, getError, safeFormatHour } from "../../../../shared/utils";
import { Claim } from "../../../../shared/interfaces/services/claim";
import { FranchiseLetterRequest } from "../../../../shared/interfaces/services/get-franchise-letter-service";
import { FRANCHISE_LETTER_STATUS } from "../../../../shared/interfaces/enums/franchise-letter-status-enum";

const formatFranchiseLetterDate = (date?: string): string => {
  if (!date) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
  }

  const formattedDate = dayjs(date);
  return formattedDate.isValid() ? formattedDate.format('DD/MM/YYYY') : date;
};


export const getClaimPDF = async (filename: string | undefined, branch: string, claimNumber: string): Promise<string | null> => {

  const ERROR_MSG_PDF = 'No se pudo obtener ni generar el documento "Denuncia de Siniestro".';
  
  const defaultFilename = `Denuncia_${claimNumber}.pdf`;
  const finalFilename = filename && filename.trim() !== '' ? filename : defaultFilename;
    
    const getFromFilenet = async (): Promise<string | null> => {
      if (!filename || filename.trim() === '') {
        return null;
      }
      
      const request: FilenetDocumentRequest = {
        documento: {
          nombreDocumento: filename,
          claseDocumental: DocumentClass.Preclaims,
          presigned: true
        },
      };
  
      try { 
        const response = await DocumentsService.getDocumentFromS3(request, false);
        const documento = response?.data?.documento;
        return documento ?? null;
      } catch (error) {
        return null;
      }
    };
  
    const getOnTheFly = async (): Promise<string | null> => {
      
      try {
            const complaintPdfDataResponse = await ClaimService.getComplaintPdfData(branch, claimNumber);
            if (!complaintPdfDataResponse) {
              getError(ERROR_MSG_PDF)
              return null;
            }

            const complaintPdfRequest = await transformClaimServiceDataToComplaintPdf(branch, complaintPdfDataResponse);
            if (!complaintPdfRequest) {
              getError(ERROR_MSG_PDF)
              return null;
            }

            const complaintPdfResponse = await DocumentsService.getComplaintPDF(complaintPdfRequest);
            const documento = complaintPdfResponse?.data?.base64;

            return documento ?? null;
          } catch (error) {
            getError(ERROR_MSG_PDF)
            return null;
          }
    };

    const presignedUrl = await getFromFilenet();
    if (presignedUrl) {
      await downloadPresignedFile(presignedUrl, finalFilename, FILE_TYPES.PDF);
      return presignedUrl;
    }

    const base64 = await getOnTheFly();
    if (!base64) {
      throw new Error(ERROR_MSG_PDF);
    }
    downloadBase64Files(base64, finalFilename, FILE_TYPES.PDF);
    return base64;
  };

  export const transformClaimServiceDataToComplaintPdf = async (branch: string, complaintPdfDataResponse: ComplaintPdfDataResponse): Promise<ComplaintPdfServiceRequest> => {

    const denunciaSiniestroResponse = complaintPdfDataResponse.denunciaSiniestro
    
    const complaintPdfServiceRequest: ComplaintPdfServiceRequest = {
        rama: Number(branch),
        ramaDescripcion: denunciaSiniestroResponse.seccion,
        numeroPredenuncia: denunciaSiniestroResponse.siniestro,
        numeroPoliza: denunciaSiniestroResponse.poliza ?  denunciaSiniestroResponse.poliza :  denunciaSiniestroResponse.asegurado?.poliza,
        numeroSiniestro: denunciaSiniestroResponse.siniestro,
        fechaHoraSiniestro: dayjs(denunciaSiniestroResponse.fechaDelSiniestro?.fecha).format('DD/MM/YYYY') +' '+ safeFormatHour(denunciaSiniestroResponse.fechaDelSiniestro?.hora),
        estadoTiempoDescripcion: denunciaSiniestroResponse.fechaDelSiniestro?.estadoDelTiempo,
        horario: denunciaSiniestroResponse.fechaDelSiniestro?.diurnoNocturno,
        lugarDelSiniestro: {
            calleNombre: denunciaSiniestroResponse.lugarDelSiniestro?.calle,
            calleNumero: denunciaSiniestroResponse.lugarDelSiniestro?.numero,
            rutaNumero: denunciaSiniestroResponse.lugarDelSiniestro?.rutaNumero,
            rutaKilometro: denunciaSiniestroResponse.lugarDelSiniestro?.kilometro,
            rutaTipo: denunciaSiniestroResponse.lugarDelSiniestro?.nacional,
            interseccionEntre1: denunciaSiniestroResponse.lugarDelSiniestro?.interseccionDe,
            interseccionEntre2: denunciaSiniestroResponse.lugarDelSiniestro?.interseccionEntre,
            cruceConRutaNumero: denunciaSiniestroResponse.lugarDelSiniestro?.cruceConRuta,
            localidadDescripcion: denunciaSiniestroResponse.lugarDelSiniestro?.localidad,
            provinciaDescripcion: denunciaSiniestroResponse.lugarDelSiniestro?.provincia,
            paisDescripcion: denunciaSiniestroResponse.lugarDelSiniestro?.pais,
            calzadaTipo: denunciaSiniestroResponse.lugarDelSiniestro?.tipoCalzada,
            calzadaEstado: denunciaSiniestroResponse.lugarDelSiniestro?.estadoCalzada,
            estadoBarrera: denunciaSiniestroResponse.lugarDelSiniestro?.barreraEstado,
            relacionConAsegurado: "",
            cruceRutaSenalizado: denunciaSiniestroResponse.lugarDelSiniestro?.senalizado === 'S' ? true : false,
            semaforoExiste: denunciaSiniestroResponse.lugarDelSiniestro?.semaforo === 'S' ? true : false,
            semaforoFunciona: denunciaSiniestroResponse.lugarDelSiniestro?.semaforoFunciona === 'S' ? true : false,
            semaforoColor: denunciaSiniestroResponse.lugarDelSiniestro?.semaforoColor,
            cruceTrenExiste: denunciaSiniestroResponse.lugarDelSiniestro?.cruceEnTren === 'S' ? true : false,
            cruceTrenSenalizado: denunciaSiniestroResponse.lugarDelSiniestro?.barreraSenalizada === 'S' ? true : false,
            barrera: denunciaSiniestroResponse.lugarDelSiniestro?.barrera === 'S' ? true : false,
            aseguradoEsConductor: denunciaSiniestroResponse.conductor?.esAsegurado === 'S' ? true : false
        },
        conductorVehiculoAsegurado: {
            nombre: denunciaSiniestroResponse.conductor?.nombreApellido,
            numeroDocumento: denunciaSiniestroResponse.conductor?.numeroDocumento,
            genero: denunciaSiniestroResponse.conductor?.genero  === '1' ? 'MASCULINO' : 'FEMENINO',
            telefono: denunciaSiniestroResponse.conductor?.telefono,
            domicilio: denunciaSiniestroResponse.conductor?.domicilio,
            codigoPostal: denunciaSiniestroResponse.conductor?.codigoPostal,
            localidad: denunciaSiniestroResponse.conductor?.localidad,
            provincia: denunciaSiniestroResponse.conductor?.provincia,
            pais: denunciaSiniestroResponse.conductor?.pais,
            estadoCivil: denunciaSiniestroResponse.conductor?.estadoCivil,
            numeroRegistro: denunciaSiniestroResponse.conductor?.registro?.numero,
            fechaNacimiento: denunciaSiniestroResponse.conductor?.fechaNacimiento,
            fechaVencimiento: denunciaSiniestroResponse.conductor?.registro.vencimiento,
            email: denunciaSiniestroResponse.conductor?.email,
            esConductorHabitualDelVehiculo: denunciaSiniestroResponse.conductor?.conductorHabitual === 'S' ? true : false,
            examenAlcoholemia: denunciaSiniestroResponse.conductor?.examenAlcoholemia,
            esConductorYPropietarioDelVehiculo: denunciaSiniestroResponse.conductor?.esAsegurado === 'S' ? true : false
        },
        asegurado: {
            nombre: denunciaSiniestroResponse.asegurado?.nombreApellido,
            numeroDocumento: denunciaSiniestroResponse.asegurado?.numeroDocumento,
            telefono: denunciaSiniestroResponse.asegurado?.telefono,
            domicilio: denunciaSiniestroResponse.asegurado?.domicilio,
            codigoPostal: denunciaSiniestroResponse.asegurado?.codigoPostal,
            localidad: denunciaSiniestroResponse.asegurado?.localidad,
            provincia: denunciaSiniestroResponse.asegurado?.provincia,
            pais: denunciaSiniestroResponse.asegurado?.pais ? denunciaSiniestroResponse.asegurado?.pais : 'ARGENTINA',
            email: denunciaSiniestroResponse.asegurado?.email,
            esConductorHabitualDelVehiculo: denunciaSiniestroResponse.conductor?.conductorHabitual === 'S' ? true : false,
            esConductorYPropietarioDelVehiculo: denunciaSiniestroResponse.conductor?.esAsegurado === 'S' ? true : false,
            nacionalidad:  denunciaSiniestroResponse.asegurado?.nacionalidad,
            cuit: denunciaSiniestroResponse.asegurado?.cuit,
            apoderado: denunciaSiniestroResponse.asegurado?.representanteLegal?.nombre,
            cuitApoderado: denunciaSiniestroResponse.asegurado?.representanteLegal?.cuit,
            numeroDocumentoApoderado: denunciaSiniestroResponse.asegurado?.representanteLegal?.documento,
        },
        datosVehiculoAsegurado: {
            propietario: denunciaSiniestroResponse.asegurado?.nombreApellido,
            marca: denunciaSiniestroResponse.vehiculoAsegurado?.marca,
            modelo: denunciaSiniestroResponse.vehiculoAsegurado?.modelo,
            dominio: denunciaSiniestroResponse.vehiculoAsegurado?.dominio,
            anio: denunciaSiniestroResponse.vehiculoAsegurado?.anio,
            tipo: denunciaSiniestroResponse.vehiculoAsegurado?.tipo,
            uso: denunciaSiniestroResponse.vehiculoAsegurado?.uso,
            numeroMotor: denunciaSiniestroResponse.vehiculoAsegurado?.motor,
            numeroChasis: denunciaSiniestroResponse.vehiculoAsegurado?.chasis,
            causaSiniestroVehiculo: denunciaSiniestroResponse.vehiculoAsegurado?.coberturaAfectada,
            detalleDaniosVehiculo: denunciaSiniestroResponse.vehiculoAsegurado?.detalleDanios,
            montoDaniosVehiculo: denunciaSiniestroResponse.vehiculoAsegurado?.montoAproximado,
            tallerMecanicoReparacion: denunciaSiniestroResponse.vehiculoAsegurado?.taller
        },
        caracteristicasDelSiniestro: transformToCharacteristics(complaintPdfDataResponse),
        terceros: transformToOthers(complaintPdfDataResponse),
        daniosMaterialesCosas: transformToThings(complaintPdfDataResponse),
        partesDaniadas: [], //No llega el valor, fix evolutivo.
        datosRiesgoAsegurado: {
            ubicacion: denunciaSiniestroResponse.riesgoAsegurado?.ubicacion,
            vigenciaDesde: dayjs(denunciaSiniestroResponse.riesgoAsegurado?.vigenciaDesde).format('DD/MM/YYYY'),
            vigenciaHasta: dayjs(denunciaSiniestroResponse.riesgoAsegurado?.vigenciaHasta).format('DD/MM/YYYY'),
            personaContactoNombre: denunciaSiniestroResponse.riesgoAsegurado?.personaContactar,
            localidad: denunciaSiniestroResponse.riesgoAsegurado?.localidad,
            productor: denunciaSiniestroResponse.riesgoAsegurado?.productor,
            personaContactoTelefono: denunciaSiniestroResponse.riesgoAsegurado?.telefono,
        },
        declaracionJurada: {
            lugar: denunciaSiniestroResponse.declaracionJurada?.lugar,
            fecha: dayjs(denunciaSiniestroResponse.declaracionJurada?.fecha).format('DD/MM/YYYY') ,
            aclaracion: '',
            numeroDocumento: ''
        },
        elementos: transformToElements(complaintPdfDataResponse)
    }

    return complaintPdfServiceRequest;
  }

  const transformToCharacteristics = (complaintPdfDataResponse: ComplaintPdfDataResponse): CaracteristicasDelSiniestro  => {
    const denunciaSiniestroResponse = complaintPdfDataResponse.denunciaSiniestro;
    if(denunciaSiniestroResponse.caracteristicas){
      return {
        tipoAccidente: denunciaSiniestroResponse.caracteristicas?.tipoAccidentes,
        colisionCon: denunciaSiniestroResponse.caracteristicas?.colision,
        kmCirculacionVehiculoAsegurado: '',
        detalleDelSiniestro: denunciaSiniestroResponse.caracteristicas?.detalles,
        denunciaPolicialExiste: denunciaSiniestroResponse.caracteristicas?.denunciaPolicial === 'S' ? true : false,
        denunciaPolicialComisaria: denunciaSiniestroResponse.caracteristicas?.comisaria,
        testigoExiste: denunciaSiniestroResponse.caracteristicas?.testigo === 'S' ? true : false,
        domicilio: denunciaSiniestroResponse.caracteristicas?.domicilio,
      }
    } else if (denunciaSiniestroResponse.detallesHecho){
      return {
        detalleDelSiniestro: denunciaSiniestroResponse.detallesHecho?.descripcion,
        denunciaPolicialExiste: denunciaSiniestroResponse.detallesHecho?.denunciaPolicial?.hubo === 'S' ?  true : false,
        denunciaPolicialComisaria: denunciaSiniestroResponse.detallesHecho?.denunciaPolicial ? denunciaSiniestroResponse.detallesHecho?.denunciaPolicial.comisaria : '',
        tipoPerdida: denunciaSiniestroResponse.detallesHecho?.tipoDePerdida,
        denunciaBomberosExiste:  denunciaSiniestroResponse.detallesHecho?.denunciaBomberos?.hubo === 'S' ? true : false,
        sumarioExiste: denunciaSiniestroResponse.detallesHecho?.sumario?.hubo === 'S' ? true : false,
        denunciaBomberosCentral: denunciaSiniestroResponse.detallesHecho?.denunciaBomberos ? denunciaSiniestroResponse.detallesHecho?.denunciaBomberos.central : '',
        juzgadoSumario: denunciaSiniestroResponse.detallesHecho?.sumario ? denunciaSiniestroResponse.detallesHecho?.sumario.juzgado : '',
        comoQuienDescubrio: denunciaSiniestroResponse.detallesHecho?.quienDescubrio,
      }
    }
    return {}
  }

const transformToThings = (complaintPdfDataResponse: ComplaintPdfDataResponse): DaniosMaterialesCosas  => {
  const denunciaSiniestroResponse = complaintPdfDataResponse.denunciaSiniestro;
  return {
    propietarioCosa:{
      nombre: denunciaSiniestroResponse.cosas?.cosa?.propietario,
      tipoDocumento: denunciaSiniestroResponse.cosas?.cosa?.tipoDocumento,
      numeroDocumento: denunciaSiniestroResponse.cosas?.cosa?.numeroDocumento,
      genero: denunciaSiniestroResponse.cosas?.cosa?.genero === '1' ? 'MASCULINO' : 'FEMENINO',
      domicilio: denunciaSiniestroResponse.cosas?.cosa?.domicilio,
      codigoPostal: denunciaSiniestroResponse.cosas?.cosa?.codigoPostal,
      localidad: denunciaSiniestroResponse.cosas?.cosa?.localidad,
      provincia: denunciaSiniestroResponse.cosas?.cosa?.provincia,
      pais: denunciaSiniestroResponse.cosas?.cosa?.pais,
      email: denunciaSiniestroResponse.cosas?.cosa?.email,
      examenAlcoholemia: ''
    }
  }
}

const transformToElements = (complaintPdfDataResponse: ComplaintPdfDataResponse): Element[] => {
  if (!complaintPdfDataResponse.denunciaSiniestro.elementos?.length) return [];
  return complaintPdfDataResponse.denunciaSiniestro.elementos.map((elemento): Element => ({
    cantidad: elemento.cantidad,
    descripcion: elemento.descripcion,
    modeloSerie: elemento.modeloSerie,
    importeEstimado: elemento.importeEstimado,
  }));
}

const transformToOthers = (complaintPdfDataResponse: ComplaintPdfDataResponse): Tercero[] => {
 
  const otrosVehiculosRaw = complaintPdfDataResponse?.denunciaSiniestro?.otrosVehiculos;
  
  const otrosVehiculos = Array.isArray(otrosVehiculosRaw)
  ? otrosVehiculosRaw
  : otrosVehiculosRaw
    ? [otrosVehiculosRaw]
    : [];

  if (!Array.isArray(otrosVehiculos) || otrosVehiculos.length === 0){
    return [];
  }

  const resultado = otrosVehiculos.map((tercero): Tercero => ({
      propietarioVehiculo: {
        nombre: tercero.vehiculo?.propietario || "",
        tipoDocumento: tercero.vehiculo?.tipoDocumento || "",
        numeroDocumento: tercero.vehiculo?.numeroDocumento || "",
        genero: tercero.vehiculo?.genero === "1" ? "MASCULINO" : "FEMENINO",
        telefono: tercero.vehiculo?.telefono || "",
        domicilio: tercero.vehiculo?.domicilio || "",
        codigoPostal: tercero.vehiculo?.codigoPostal || "",
        localidad: tercero.vehiculo?.localidad || "",
        provincia: tercero.vehiculo?.provincia || "",
        pais: tercero.vehiculo?.pais || "",
        estadoCivil: "",
        numeroRegistro: "",
        fechaNacimiento: "",
        fechaVencimiento: "",
        email: tercero.vehiculo?.Conductor?.email || "",
        esConductorHabitualDelVehiculo: false,
        examenAlcoholemia: tercero.vehiculo?.examenAlcoholemia || "",
        esConductorYPropietarioDelVehiculo: tercero.vehiculo?.espropietario === "S"
      },
      conductorVehiculo: {
        nombre: tercero.vehiculo?.Conductor?.nombreApellido || "",
        tipoDocumento: tercero.vehiculo?.Conductor?.tipoDocumento || "",
        numeroDocumento: tercero.vehiculo?.Conductor?.numeroDocumento || "",
        genero: tercero.vehiculo?.Conductor?.genero === "1" ? "MASCULINO" : "FEMENINO",
        telefono: tercero.vehiculo?.Conductor?.telefono || "",
        domicilio: tercero.vehiculo?.Conductor?.domicilio || "",
        codigoPostal: tercero.vehiculo?.Conductor?.codigoPostal || "",
        localidad: tercero.vehiculo?.Conductor?.localidad || "",
        provincia: tercero.vehiculo?.Conductor?.provincia || "",
        pais: tercero.vehiculo?.Conductor?.pais || "",
        estadoCivil: tercero.vehiculo?.Conductor?.estadoCivil || "",
        numeroRegistro: tercero.vehiculo?.Conductor?.registro.numero || "",
        fechaNacimiento: tercero.vehiculo?.Conductor?.fechaNacimiento || "",
        fechaVencimiento: tercero.vehiculo?.Conductor?.registro.vencimiento || "",
      },
      vehiculo: {
        propietario: tercero.vehiculo?.propietario || "",
        marca: tercero.vehiculo?.marca || "",
        modelo: tercero.vehiculo?.modelo || "",
        dominio: tercero.vehiculo?.dominio || "",
        anio: tercero.vehiculo?.anio || "",
        tipo: tercero.vehiculo?.tipo || "",
        uso: tercero.vehiculo?.uso || "",
        numeroMotor: tercero.vehiculo?.motor || "",
        numeroChasis: tercero.vehiculo?.chasis || ""
      },
      huboLesiones: false, //No llega del BACK
      tipoLesion: "", //No llega del BACK
      transportado: "" //No llega del BACK
  }));
  
  return resultado;
};

export const getFranchiseLetter = async (claim : Claim ): Promise<string | null> => {
    if (claim?.cartaFranquicia?.estado !== FRANCHISE_LETTER_STATUS.FRANCHISE_LETTER_IS_DONE) {
      throw new Error('La carta de franquicia solo se puede descargar cuando está disponible.');
    }

    const getFromFilenet = async (): Promise<string | null> => {
      const request: FranchiseLetterRequest = {
        asegurado: claim.asegurado,
        bienSiniestrado: claim.bienSiniestrado,
        fechaSiniestro: dayjs(claim.fechaSiniestro, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        poliza:claim.poliza,
        vigenciaDesde: formatFranchiseLetterDate(claim.cartaFranquicia?.vigenciaDesde),
        vigenciaHasta: formatFranchiseLetterDate(claim.cartaFranquicia?.vigenciaHasta),
        franquicia: claim.cartaFranquicia
      };

      try { 
        const response = await DocumentsService.getFranchiseLetter(request);
        const documento = response?.data?.base64;
        return documento ?? null;
      } catch (error) {
        return null;
      }
    };

    const base64 = await getFromFilenet() ?? "";

    downloadBase64Files(base64, "CartaFranquicia_" + claim.nroSiniestro, FILE_TYPES.PDF);
    return base64;
  };

export const getCoverageCertificatePDF = async (claim: Claim): Promise<string | null> => {

  const ERROR_MSG_PDF = 'No se pudo obtener el documento "Constancia de Cobertura de Vehículo".';

  try {
    // Llamar al servicio getCoverageCertificate
    const response = await ClaimService.getCoverageCertificate({
      rama: claim.rama,
      poliza: claim.poliza,
      siniestro: claim.nroSiniestro,
    });

    if (!response.data) {
      throw new Error(ERROR_MSG_PDF);
    }

    const formatAmount = (value: string): string => {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (value: string): string => {
      // Accepts DD-MM-YYYY or YYYY-MM-DD and returns DD/MM/YYYY
      if (!value) return value;
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value.replace(/-/g, '/');
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-');
        return `${d}/${m}/${y}`;
      }
      return value;
    };

    const vehicles: any[] = response.data.bienes?.map((item: any) => ({
      marca: item.marca || '',
      modelo: item.modelo || '',
      anio: String(item.anio || ''),
      tipo: item.tipo || '',
      uso: item.uso || '',
      motor: item.motor || '',
      chasis: item.chasis || '',
      carroceria: item.carroceria || '',
      patente: item.patente || '',
      valorAsegurado: item.valorAsegurado ? formatAmount(item.valorAsegurado) : '',
      cobertura: item.cobertura || '',
      franquicia: item.franquicia ? formatAmount(item.franquicia) : '',
      acreedorPrendario: item.acreedorPrendario || '',
      moneda: item.moneda || '',
      descripcionDeCobertura: item.detalle || '',
    })) || [];

    const coverageConstancyRequest = {
      rama: response.data.rama,
      poliza: String(response.data.poliza),
      vigenciaDesde: formatDate(response.data.vigenciaDesde),
      vigenciaHasta: formatDate(response.data.vigenciaHasta),
      fechaHoraReporte: dayjs().format('DD/MM/YYYY HH:mm:ss'),
      asegurado: response.data.asegurado,
      domicilio: [response.data.domicilio, response.data.localidad, response.data.provincia].filter(Boolean).join(', '),
      productor: response.data.productor,
      productorCodigo: String(response.data.codigoProductor),
      endoso: String(response.data.endoso),
      bienes: vehicles,
    };

    const documentResponse = await DocumentsService.getDocumentCarCoverage(coverageConstancyRequest);

    const base64 = documentResponse?.data?.base64;
    
    if (!base64) {
      throw new Error(ERROR_MSG_PDF);
    }

    const filename = `ConstanciaDeCobertura_${claim.nroSiniestro}.pdf`;
    
    downloadBase64Files(base64, filename, FILE_TYPES.PDF);
    return base64;
  } catch (error) {
    throw new Error(getError(error));
  }
};
